import { Box, Text } from "@chakra-ui/react"
import { toUserTZ } from "@hydrologiq/common/helpers/date"
import Figure from "@hydrologiq/shared/figures/generic/Figure"
import FigureContent from "@hydrologiq/shared/figures/generic/FigureContent"
import FigureGrid from "@hydrologiq/shared/figures/generic/FigureGrid"
import { useForecastContext } from "./ForecastProvider"
import { useMemo } from "react"
import { DateTime } from "timezonecomplete"

interface AssetForecastSummaryProps {
  entityType: "asset"
  assetId: string
  days?: number
  start?: DateTime
}

interface InstallationForecastSummaryProps {
  entityType: "installation"
  installationId: string
  days?: number
  start?: DateTime
}

type ForecastSummaryProps = AssetForecastSummaryProps | InstallationForecastSummaryProps

function formatDate(dateString: string | null, hourly = false): string {
  if (!dateString) return "Not within forecast period"

  const date = toUserTZ(dateString)
  return date.format(hourly ? "MMM dd - h a" : "MMM dd")
}

function SummaryCard({
  label,
  value,
  loading,
  subtext = "estimated",
  additionalInfo,
}: {
  label: string
  value: string
  loading: boolean
  subtext?: string
  additionalInfo?: React.ReactNode
}) {
  return (
    <Figure heading={label} loading={loading}>
      <FigureContent.Root>
        <FigureContent.SubText>{subtext}</FigureContent.SubText>
        <FigureContent.Value>{value}</FigureContent.Value>
        {additionalInfo && <FigureContent.Label>{additionalInfo}</FigureContent.Label>}
      </FigureContent.Root>
    </Figure>
  )
}

export default function ForecastSummary(props: ForecastSummaryProps) {
  // Use shared forecast data from provider
  const { dailyForecast, hourlyForecast, dailyLoading, hourlyLoading } = useForecastContext()

  const loading = dailyLoading || hourlyLoading


  // Determine which forecast is more conservative (has lower run dry time)
  const isHourlyMoreConservative = useMemo(() => {
    if (hourlyForecast?.runDryMinutes && hourlyForecast?.runDryMinutes <= 5 * 24 * 60) return true
    if (!dailyForecast?.runDryMinutes && hourlyForecast?.runDryMinutes) return true
    return false
  }, [hourlyForecast])

  // Use the more conservative (lower) run_dry value in MINUTES for precise comparison
  // Handle the case where runDryMinutes could be 0 (immediate depletion)
  const runDryMinutes = useMemo(() => {
    return (isHourlyMoreConservative ? hourlyForecast?.runDryMinutes : dailyForecast?.runDryMinutes) ?? null
  }, [dailyForecast, hourlyForecast, isHourlyMoreConservative])

  // Use consumption from whichever forecast is being displayed (the more conservative one)
  const avgDailyConsumption = useMemo(() => {
    if (isHourlyMoreConservative) {
      return hourlyForecast?.summary?.averageDailyConsumption
    }
    return dailyForecast?.summary?.averageDailyConsumption
  }, [isHourlyMoreConservative, dailyForecast, hourlyForecast])

  // Use current level from whichever forecast is available (they should be the same)
  const currentLevel = dailyForecast?.currentLevel ?? hourlyForecast?.currentLevel

  // Use run dry date from the forecast that provided the conservative run_dry time
  const runDryDate = useMemo(() => {
    if (isHourlyMoreConservative) {
      return hourlyForecast?.runDryDate
    }
    return dailyForecast?.runDryDate
  }, [isHourlyMoreConservative, dailyForecast, hourlyForecast])

  // Display logic: show hours if < 3 days
  const DAY_THRESHOLD = 3 * 24 * 60
  const displayInHours = (runDryMinutes !== null && runDryMinutes < DAY_THRESHOLD && isHourlyMoreConservative) ? true : false



  return (
    <FigureGrid>
      <SummaryCard
        label="Hydrogen Level"
        value={currentLevel ? `${currentLevel.toFixed(1)} kg` : "-"}
        loading={loading}
        subtext="current"
      />
      <SummaryCard
        label="Consumption"
        value={avgDailyConsumption ? `${avgDailyConsumption.toFixed(1)} kg` : "-"}
        loading={loading}
        subtext="average"
        additionalInfo={
          <Box fontSize="sm" color="fg.muted">
            per day
          </Box>
        }
      />
      <SummaryCard
        label="Asset Depleted in"
        value={runDryDate ? formatDate(runDryDate, displayInHours) : "-"}
        loading={loading}
        additionalInfo={
          <Text fontSize="sm" color="fg.muted">
            {runDryMinutes !== null
              ? displayInHours
                ? runDryMinutes <= 0
                  ? "Less than 1 hour"
                  : `${Math.floor(runDryMinutes / 60)} hours of consumption`
                : runDryMinutes <= 0
                  ? "Today"
                  : `${Math.floor(runDryMinutes / (24 * 60))} days of consumption`
              : 'Calculating...'}
          </Text>
        }
      />
    </FigureGrid>
  )
}